import { useEffect, useState, useRef } from "react";
import { FaChevronLeft, FaChevronRight, FaImage, FaMapMarkerAlt, FaTimes, FaSearch } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { MapSelector } from "../../components/map";
import MapDisplay from "../../components/map/MapDisplay";
import InteractiveMapDisplay from "../../components/map/InteractiveMapDisplay";
import { createPlace } from "../../services/placeService";
import { createPost } from "../../services/postService";
import { compressImage } from "../../utils/imageOptimizer";
import { getMapboxToken, comprehensiveSearch } from "../../services/mapService";
import supabase from '../../services/supabase';
import "./NewPost.css";

export default function NewPost() {
  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [country, setCountry] = useState("");
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [content, setContent] = useState("");
  const [wordCount, setWordCount] = useState(0);
  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [currentPreviewIndex, setCurrentPreviewIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [showMapSelector, setShowMapSelector] = useState(false);
  const [mapLocation, setMapLocation] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const searchTimeoutRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is authenticated
    const getUser = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          navigate("/login");
          return;
        }
        setUser(session.user);
      } catch (error) {
        console.error("Authentication error:", error);
        alert("Authentication error. Please try logging in again.");
        navigate("/login");
      }
    };
    
    getUser();
  }, [navigate]);

  // If a location is selected, update the country field automatically
  useEffect(() => {
    if (mapLocation && mapLocation.name) {
      // Extract country from location name (e.g., "Istanbul, Turkey" -> "Turkey")
      const locationParts = mapLocation.name.split(', ');
      if (locationParts.length > 1) {
        setCountry(locationParts[locationParts.length - 1]);
      }
    }
  }, [mapLocation]);

  // Handle clicks outside search results
  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
      // Cleanup search timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);
  
  // Generate image previews when images are selected
  useEffect(() => {
    const generatePreviews = async () => {
      const previews = [];
      
      for (const image of images) {
        const reader = new FileReader();
        const preview = await new Promise((resolve) => {
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(image);
        });
        previews.push(preview);
      }
      
      setImagePreviews(previews);
    };
    
    if (images.length > 0) {
      generatePreviews();
    } else {
      setImagePreviews([]);
      setCurrentPreviewIndex(0);
    }
  }, [images]);

  const handleAddTag = () => {
    // Trim and sanitize tag input
    const sanitizedTag = tagInput.trim();
    if (sanitizedTag && !tags.includes(sanitizedTag)) {
      setTags([...tags, sanitizedTag]);
      setTagInput("");
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleTagInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  // Function to count words in text
  const countWords = (text) => {
    if (!text.trim()) return 0;
    return text.trim().split(/\s+/).length;
  };

  // Handle content change with word count
  const handleContentChange = (e) => {
    const newContent = e.target.value;
    const newWordCount = countWords(newContent);
    
    // Only update if under word limit or if user is deleting text
    if (newWordCount <= 1000 || newContent.length < content.length) {
      setContent(newContent);
      setWordCount(newWordCount);
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    
    // Check if adding these files would exceed the limit of 10 images
    if (images.length + files.length > 10) {
      alert("You can upload a maximum of 10 images per post.");
      return;
    }
    
    const validFiles = [];
    
    // Validate and compress each file
    for (const file of files) {
      const fileType = file.type;
      const validImageTypes = [
        'image/jpeg',
        'image/png',
        'image/gif',
        'image/webp',
        'image/dng',
        'application/x-adobe-dng'
      ];
      
      if (!validImageTypes.includes(fileType)) {
        alert(`File "${file.name}" is not a valid image type (JPEG, PNG, GIF, DNG, or WEBP).`);
        continue;
      }
      
      // 5MB max size
      if (file.size > 5 * 1024 * 1024) {
        alert(`File "${file.name}" exceeds 5MB limit. Please upload a smaller image.`);
        continue;
      }
      
      try {
        // Compress image if it's a JPEG or PNG
        if (fileType === 'image/jpeg' || fileType === 'image/png') {
          const compressedBlob = await compressImage(file, 0.8, 1200);
          // Convert blob back to File object with original name
          const compressedFile = new File([compressedBlob], file.name, {
            type: 'image/jpeg',
            lastModified: Date.now()
          });
          validFiles.push(compressedFile);
        } else {
          validFiles.push(file);
        }
      } catch (error) {
        console.error('Error compressing image:', error);
        // Fallback to original file if compression fails
        validFiles.push(file);
      }
    }
    
    // Add valid files to the existing images array
    setImages(prevImages => [...prevImages, ...validFiles]);
  };

  const handleRemoveImage = (indexToRemove) => {
    setImages(prevImages => prevImages.filter((_, index) => index !== indexToRemove));
    
    // Adjust current preview index if necessary
    if (currentPreviewIndex >= indexToRemove && currentPreviewIndex > 0) {
      setCurrentPreviewIndex(prev => prev - 1);
    }
  };

  const handlePrevImage = () => {
    setCurrentPreviewIndex(prev => (prev > 0 ? prev - 1 : images.length - 1));
  };

  const handleNextImage = () => {
    setCurrentPreviewIndex(prev => (prev < images.length - 1 ? prev + 1 : 0));
  };

  const handleMoveImage = (fromIndex, direction) => {
    // Create a copy of the images array
    const updatedImages = [...images];
    
    // Calculate toIndex based on direction
    const toIndex = direction === 'left' ? fromIndex - 1 : fromIndex + 1;
    
    // Check if the move is valid
    if (toIndex < 0 || toIndex >= updatedImages.length) {
      return;
    }
    
    // Swap the images
    const temp = updatedImages[fromIndex];
    updatedImages[fromIndex] = updatedImages[toIndex];
    updatedImages[toIndex] = temp;
    
    // Update the state
    setImages(updatedImages);
    
    // Follow the moved image in the preview
    setCurrentPreviewIndex(toIndex);
  };

  const handleBackButton = () => {
    navigate("/profile");
  };

  const handleMapButton = () => {
    setShowMapSelector(true);
  };

  const handleSelectLocation = (location) => {
    setMapLocation(location);
    setShowMapSelector(false);
  };

  const handleCloseMap = () => {
    setShowMapSelector(false);
  };

  const handleRemoveLocation = () => {
    setMapLocation(null);
    setCountry("");
    setSearchQuery("");
  };

  // Search for locations using Mapbox geocoding
  const searchLocations = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    setShowSearchResults(true);

    try {
      const suggestions = await comprehensiveSearch(query, { limit: 8 });
      setSearchResults(suggestions);
      setIsSearching(false);
    } catch (error) {
      console.error('Error searching locations:', error);
      setSearchResults([]);
      setIsSearching(false);
    }
  };

  // Handle search input changes
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    if (query.trim()) {
      // Debounce the search
      searchTimeoutRef.current = setTimeout(() => {
        searchLocations(query);
      }, 300);
    } else {
      setSearchResults([]);
      setShowSearchResults(false);
      setIsSearching(false);
    }
  };

  // Handle selecting a search result
  const handleSelectSearchResult = (result) => {
    const location = {
      name: result.place_name,
      lat: result.center[1],
      lng: result.center[0]
    };
    
    setMapLocation(location);
    setSearchQuery(result.place_name);
    setShowSearchResults(false);
    
    // Extract country from place_name
    const placeParts = result.place_name.split(', ');
    if (placeParts.length > 1) {
      setCountry(placeParts[placeParts.length - 1]);
    }
  };

  // Handle search input focus
  const handleSearchFocus = () => {
    if (searchResults.length > 0) {
      setShowSearchResults(true);
    }
  };

  // Handle clicking outside search results
  const handleClickOutside = (e) => {
    if (!e.target.closest('.search-input-container') && !e.target.closest('.search-results')) {
      setShowSearchResults(false);
    }
  };

  // Handle pin placement on interactive map
  const handleMapPinPlacement = (location) => {
    console.log('Pin placed, updating search bar with:', location.name);
    
    // For ALL locations (both real places and custom coordinates), put them in the search bar
    setSearchQuery(location.name);
    setMapLocation(null); // Keep in search mode for all locations
    setShowSearchResults(false);
  };

  const handleSubmit = async () => {
    // Validate required fields
    if (!title.trim()) {
      alert("Please enter a title");
      return;
    }

    if (!content.trim()) {
      alert("Please write some content for your post");
      return;
    }

    // Validate word limit
    if (wordCount > 1000) {
      alert("Please reduce your content to 1000 words or less");
      return;
    }

    // Validate if location is selected
    if (!mapLocation) {
      alert("Please select a location for your post");
      return;
    }

    setLoading(true);

    try {
      // Get user ID from authentication
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error("User not authenticated");
      }
      
      // Format tags for storage
      const tagsString = tags.length > 0 ? tags.join(',') : '';
      
      // Prepare location data if we have it
      let locationData = null;
      if (mapLocation) {
        locationData = {
          name: mapLocation.name,
          lat: mapLocation.lat,
          lng: mapLocation.lng
        };
        console.log("Location data for post:", locationData);
      }
      
      // Create post data
      const postData = {
        title,
        content,
        user_id: user.id,
        tags: tagsString,
        post_date: date || new Date().toISOString().split('T')[0],
        country
      };
      
      console.log("Creating post with data:", postData);
      
      // Create post with location data
      const newPost = await createPost(postData, locationData);
      const postId = newPost.id;
      console.log("Created post with ID:", postId);
      
      // Upload images if any
      if (images.length > 0) {
        console.log(`Uploading ${images.length} images...`);
        console.log('Images array:', images.map((img, idx) => ({ index: idx, name: img.name, type: img.type, size: img.size })));
        
        for (let i = 0; i < images.length; i++) {
          const file = images[i];
          
          // Validate file object
          if (!file) {
            console.error(`No file at index ${i}`);
            throw new Error(`No file at position ${i + 1}. Please try uploading the image again.`);
          }
          
          if (!file.name) {
            console.error(`File at index ${i} has no name:`, file);
            // Generate a default name if missing
            file.name = `image_${i + 1}.jpg`;
          }
          
          const fileName = `${postId}_${i}_${Date.now()}`;
          const fileExt = file.name.split('.').pop() || 'jpg'; // Default to jpg if no extension
          const filePath = `${fileName}.${fileExt}`;

          console.log(`Uploading image ${i + 1}/${images.length}: ${filePath}`);

          // Upload to storage
          const { error: uploadError } = await supabase.storage
            .from('post-images')
            .upload(filePath, file);

          if (uploadError) {
            console.error('Error uploading image:', uploadError);
            throw uploadError;
          }

          // Get public URL
          const { data: { publicUrl } } = supabase.storage
            .from('post-images')
            .getPublicUrl(filePath);

          console.log(`Generated public URL: ${publicUrl}`);

          // Create entry in post_media table
          const { error: mediaError } = await supabase
            .from('post_media')
            .insert([{
              post_id: postId,
              media_type: 'image',
              url: publicUrl,
              display_order: i,
              caption: null
            }]);

          if (mediaError) {
            console.error('Error creating media entry:', mediaError);
            throw mediaError;
          }

          console.log(`Successfully uploaded image ${i + 1}`);
        }
        
        console.log("All images uploaded successfully");
      }
      
      // Redirect to feed
      navigate("/feed");
      
    } catch (error) {
      console.error("Error creating post:", error);
      
      let errorMessage = "Failed to create post. Please try again.";
      if (error.message) {
        errorMessage = `Error: ${error.message}`;
      }
      if (error.details) {
        errorMessage += `\nDetails: ${error.details}`;
      }
      
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Show loading state during authentication check
  if (!user && !loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="new-post-container wanderlog-style">
      <div className="post-header">
        <button className="back-button" onClick={handleBackButton}>
          Back
        </button>
        <h1>Create New Post</h1>
        <button 
          className="publish-button" 
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? "Publishing..." : "Publish"}
        </button>
      </div>
      
      <div className="two-column-layout">
        {/* Left column: Post content */}
        <div className="post-content-column">
          <div className="form-group">
            <label htmlFor="title">Title</label>
            <input
              type="text"
              id="title"
              placeholder="Add a title to your post"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="content">Content</label>
            <textarea
              id="content"
              placeholder="Share your travel experience..."
              value={content}
              onChange={handleContentChange}
              rows={8}
              required
              className={wordCount > 1000 ? 'content-textarea-error' : ''}
            />
            <div className={`word-count ${wordCount > 1000 ? 'word-count-error' : ''}`}>
              {wordCount}/1000 words
              {wordCount > 1000 && <span className="word-limit-warning"> - Word limit exceeded!</span>}
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="upload-image">Images (Maximum: 10)</label>
            <div className="image-upload-container">
              {imagePreviews.length > 0 ? (
                <div className="image-carousel-container">
                  <div className="image-preview-container">
                    <img 
                      src={imagePreviews[currentPreviewIndex]} 
                      alt="Preview" 
                      className="image-preview" 
                    />
                    <button 
                      className="remove-image-btn" 
                      onClick={() => handleRemoveImage(currentPreviewIndex)}
                    >
                      <FaTimes />
                    </button>
                    
                    {/* Image navigation buttons */}
                    {images.length > 1 && (
                      <>
                        <button 
                          className="carousel-nav-btn carousel-prev-btn" 
                          onClick={handlePrevImage}
                        >
                          <FaChevronLeft />
                        </button>
                        <button 
                          className="carousel-nav-btn carousel-next-btn" 
                          onClick={handleNextImage}
                        >
                          <FaChevronRight />
                        </button>
                      </>
                    )}
                    
                    {/* Image reorder buttons */}
                    <div className="image-reorder-controls">
                      {currentPreviewIndex > 0 && (
                        <button 
                          className="reorder-btn" 
                          onClick={() => handleMoveImage(currentPreviewIndex, 'left')}
                        >
                          Move Left
                        </button>
                      )}
                      {currentPreviewIndex < images.length - 1 && (
                        <button 
                          className="reorder-btn" 
                          onClick={() => handleMoveImage(currentPreviewIndex, 'right')}
                        >
                          Move Right
                        </button>
                      )}
                    </div>
                    
                    {/* Image indicator dots */}
                    <div className="image-indicators">
                      {images.map((_, index) => (
                        <span 
                          key={index} 
                          className={`indicator-dot ${index === currentPreviewIndex ? 'active' : ''}`}
                          onClick={() => setCurrentPreviewIndex(index)}
                        />
                      ))}
                    </div>
                  </div>
                  
                  {/* Add more images button if under limit */}
                  {images.length < 10 && (
                    <label className="add-more-images-label" htmlFor="upload-image">
                      <FaImage className="upload-icon" />
                      <span>Add more images ({images.length}/10)</span>
                    </label>
                  )}
                </div>
              ) : (
                <label className="upload-image-label" htmlFor="upload-image">
                  <FaImage className="upload-icon" />
                  <span>Click to upload images (up to 10)</span>
                </label>
              )}
              <input
                type="file"
                id="upload-image"
                onChange={handleImageUpload}
                accept="image/jpeg, image/png, image/gif, image/webp, image/dng, application/x-adobe-dng, .dng"
                style={{ display: "none" }}
                multiple
              />
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="tags">Tags (Optional)</label>
            <div className="tag-input-container">
              <input
                type="text"
                id="tags"
                placeholder="Add tags (e.g., beach, hiking)"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleTagInputKeyDown}
              />
              <button className="add-tag-btn" onClick={handleAddTag}>
                Add
              </button>
            </div>
            {tags.length > 0 && (
              <div className="tags-container">
                {tags.map((tag, index) => (
                  <div key={index} className="tag">
                    <span>#{tag}</span>
                    <button
                      className="remove-tag-btn"
                      onClick={() => handleRemoveTag(tag)}
                    >
                      <FaTimes />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div className="form-group">
            <label htmlFor="post-date">Date (Optional)</label>
            <input
              type="date"
              id="post-date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>
        </div>
        
        {/* Right column: Map */}
        <div className="map-column">
          <div className="form-group">
            <label>Location</label>
            {mapLocation ? (
              <div className="selected-location">
                <div className="location-info">
                  <FaMapMarkerAlt className="location-icon" />
                  <span>{mapLocation.name}</span>
                </div>
                <button
                  className="remove-location-btn"
                  onClick={handleRemoveLocation}
                >
                  <FaTimes />
                </button>
              </div>
            ) : (
              <>
                <div className="search-input-container">
                  <FaSearch className="search-icon" />
                  <input
                    type="text"
                    placeholder="Search for a location or click on the map to place a pin..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    onFocus={handleSearchFocus}
                    className="location-search-input"
                  />
                               {searchQuery && (
               <button
                 className="remove-location-btn"
                 onClick={() => {
                   setSearchQuery("");
                   setMapLocation(null);
                 }}
                 style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)' }}
               >
                 <FaTimes />
               </button>
             )}
                </div>
                {showSearchResults && (searchResults.length > 0 || isSearching) && (
                  <div className={`search-results ${isSearching ? 'loading' : ''}`}>
                    {isSearching ? null : searchResults.map((result, index) => (
                      <div
                        key={index}
                        className="search-result-item"
                        onClick={() => handleSelectSearchResult(result)}
                      >
                        <FaMapMarkerAlt className="result-icon" />
                        <div className="result-text">
                          <div className="result-name">{result.text}</div>
                          <div className="result-place">{result.place_name}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
          
          {/* Always show map - either with selected location or interactive map */}
          <div className="map-preview">
            {mapLocation ? (
              <MapDisplay
                location={mapLocation}
                zoomLevel={12}
                title="Post location"
              />
            ) : searchQuery && searchQuery.startsWith('Custom Location') ? (
              // Show custom location on map
              <MapDisplay
                location={{
                  lat: parseFloat(searchQuery.match(/\(([^,]+), ([^)]+)\)/)?.[1] || 0),
                  lng: parseFloat(searchQuery.match(/\(([^,]+), ([^)]+)\)/)?.[2] || 0),
                  name: searchQuery
                }}
                zoomLevel={12}
                title="Custom location"
              />
            ) : (
              <InteractiveMapDisplay
                location={null}
                onLocationSelect={handleMapPinPlacement}
                zoom={1}
                title="Click to place pin"
              />
            )}
          </div>
        </div>
      </div>
      
      {/* Map Selector Modal */}
      {showMapSelector && (
        <div className="map-selector-modal">
          <div className="map-selector-content">
            <div className="map-selector-header">
              <h2>Select Location</h2>
              <button
                className="close-map-btn"
                onClick={handleCloseMap}
              >
                <FaTimes />
              </button>
            </div>
            <MapSelector
              onSelectLocation={handleSelectLocation}
              onClose={handleCloseMap}
            />
          </div>
        </div>
      )}
    </div>
  );
}